import React, { useState } from 'react';
import AttachmentUpload from './AttachmentUpload';

const CarRentalForm = ({ onSave, onCancel, initialData }) => {
  const [formData, setFormData] = useState({
    title: initialData?.title || '',
    componentType: 'Car Rental',
    agencyName: initialData?.agencyName || '',
    description: initialData?.description || '',
    rentalDetails: initialData?.rentalDetails || '',
    carClass: initialData?.carClass || '',
    carSeats: initialData?.carSeats || 0,
    pickupLocation: initialData?.pickupLocation || '',
    pickupDate: initialData?.pickupDate || '',
    pickupTime: initialData?.pickupTime || '',
    dropoffLocation: initialData?.dropoffLocation || '',
    dropoffDate: initialData?.dropoffDate || '',
    dropoffTime: initialData?.dropoffTime || '',
    confirmationNumber: initialData?.confirmationNumber || '',
    phoneNumber: initialData?.phoneNumber || '',
    price: initialData?.price || '',
    depositRequired: initialData?.depositRequired || false,
    depositAmount: initialData?.depositAmount || '',
    depositDueDate: initialData?.depositDueDate || '',
    isOption: initialData?.isOption || false,
    optionDescription: initialData?.optionDescription || '',
    paymentTiming: initialData?.paymentTiming || 'with_deposit',
    optionalDepositAmount: initialData?.optionalDepositAmount || '',
    photos: initialData?.photos || [],
    documents: initialData?.documents || []
  });

  const [errors, setErrors] = useState({});

  const carClassOptions = [
    'Economy',
    'Compact',
    'Midsize',
    'Standard',
    'Full-size',
    'Premium',
    'Luxury',
    'SUV',
    'Minivan',
    'Convertible'
  ];

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.agencyName.trim()) {
      newErrors.agencyName = 'Agency name is required';
    }
    
    if (!formData.rentalDetails.trim()) {
      newErrors.rentalDetails = 'Rental details are required';
    }
    
    if (!formData.carClass) {
      newErrors.carClass = 'Car class is required';
    }
    
    if (!formData.pickupLocation.trim()) {
      newErrors.pickupLocation = 'Pickup location is required';
    }
    
    if (!formData.pickupDate) {
      newErrors.pickupDate = 'Pickup date is required';
    }
    
    if (!formData.dropoffLocation.trim()) {
      newErrors.dropoffLocation = 'Drop-off location is required';
    }
    
    if (!formData.dropoffDate) {
      newErrors.dropoffDate = 'Drop-off date is required';
    }
    
    // Validate that drop-off is after pickup
    if (formData.pickupDate && formData.dropoffDate) {
      const pickupDateTime = new Date(`${formData.pickupDate}T${formData.pickupTime || '00:00'}`);
      const dropoffDateTime = new Date(`${formData.dropoffDate}T${formData.dropoffTime || '00:00'}`);
      
      if (dropoffDateTime <= pickupDateTime) {
        newErrors.dropoffDate = 'Drop-off must be after pickup';
      }
    }
    
    // Validate car seats
    if (formData.carSeats < 0 || formData.carSeats > 8) {
      newErrors.carSeats = 'Car seats must be between 0-8';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    
    onSave({
      ...formData,
      type: 'car',
      price: parseFloat(formData.price) || 0,
      carSeats: parseInt(formData.carSeats) || 0,
      depositAmount: formData.depositRequired ? parseFloat(formData.depositAmount) || 0 : 0,
      paymentTiming: formData.paymentTiming,
      optionalDepositAmount: formData.optionalDepositAmount ? parseFloat(formData.optionalDepositAmount) : null
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="component-form car-rental-form">
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="title">Component Title *</label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            placeholder="e.g., 7-Day Economy Car Rental"
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="componentType">Component Type</label>
          <input
            type="text"
            id="componentType"
            name="componentType"
            value={formData.componentType}
            className="readonly-input"
            disabled
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="agencyName">Rental Agency *</label>
          <input
            type="text"
            id="agencyName"
            name="agencyName"
            value={formData.agencyName}
            onChange={handleInputChange}
            placeholder="e.g., Enterprise, Hertz, Avis"
            className={errors.agencyName ? 'error' : ''}
            required
          />
          {errors.agencyName && <div className="error-message">{errors.agencyName}</div>}
        </div>
        <div className="form-group">
          <label htmlFor="description">Description</label>
          <input
            type="text"
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Brief description of the rental"
          />
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="rentalDetails">Rental Details *</label>
        <textarea
          id="rentalDetails"
          name="rentalDetails"
          value={formData.rentalDetails}
          onChange={handleInputChange}
          placeholder="Detailed information about the car rental, terms, conditions, what's included, etc."
          rows="4"
          className={errors.rentalDetails ? 'error' : ''}
          required
        />
        {errors.rentalDetails && <div className="error-message">{errors.rentalDetails}</div>}
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="carClass">Car Class *</label>
          <select
            id="carClass"
            name="carClass"
            value={formData.carClass}
            onChange={handleInputChange}
            className={errors.carClass ? 'error' : ''}
            required
          >
            <option value="">Select car class</option>
            {carClassOptions.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
          {errors.carClass && <div className="error-message">{errors.carClass}</div>}
        </div>
        <div className="form-group">
          <label htmlFor="carSeats">Car Seats</label>
          <input
            type="number"
            id="carSeats"
            name="carSeats"
            value={formData.carSeats}
            onChange={handleInputChange}
            placeholder="0"
            min="0"
            max="8"
            className={errors.carSeats ? 'error' : ''}
          />
          {errors.carSeats && <div className="error-message">{errors.carSeats}</div>}
        </div>
      </div>

      <div className="form-section">
        <h4>Pickup Information</h4>
        <div className="form-group">
          <label htmlFor="pickupLocation">Pickup Location *</label>
          <input
            type="text"
            id="pickupLocation"
            name="pickupLocation"
            value={formData.pickupLocation}
            onChange={handleInputChange}
            placeholder="e.g., LAX Airport, Downtown Office"
            className={errors.pickupLocation ? 'error' : ''}
            required
          />
          {errors.pickupLocation && <div className="error-message">{errors.pickupLocation}</div>}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="pickupDate">Pickup Date *</label>
            <input
              type="date"
              id="pickupDate"
              name="pickupDate"
              value={formData.pickupDate}
              onChange={handleInputChange}
              className={errors.pickupDate ? 'error' : ''}
              required
            />
            {errors.pickupDate && <div className="error-message">{errors.pickupDate}</div>}
          </div>
          <div className="form-group">
            <label htmlFor="pickupTime">Pickup Time</label>
            <input
              type="time"
              id="pickupTime"
              name="pickupTime"
              value={formData.pickupTime}
              onChange={handleInputChange}
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h4>Drop-off Information</h4>
        <div className="form-group">
          <label htmlFor="dropoffLocation">Drop-off Location *</label>
          <input
            type="text"
            id="dropoffLocation"
            name="dropoffLocation"
            value={formData.dropoffLocation}
            onChange={handleInputChange}
            placeholder="e.g., LAX Airport, Downtown Office"
            className={errors.dropoffLocation ? 'error' : ''}
            required
          />
          {errors.dropoffLocation && <div className="error-message">{errors.dropoffLocation}</div>}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="dropoffDate">Drop-off Date *</label>
            <input
              type="date"
              id="dropoffDate"
              name="dropoffDate"
              value={formData.dropoffDate}
              onChange={handleInputChange}
              className={errors.dropoffDate ? 'error' : ''}
              required
            />
            {errors.dropoffDate && <div className="error-message">{errors.dropoffDate}</div>}
          </div>
          <div className="form-group">
            <label htmlFor="dropoffTime">Drop-off Time</label>
            <input
              type="time"
              id="dropoffTime"
              name="dropoffTime"
              value={formData.dropoffTime}
              onChange={handleInputChange}
            />
          </div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="confirmationNumber">Confirmation Number</label>
          <input
            type="text"
            id="confirmationNumber"
            name="confirmationNumber"
            value={formData.confirmationNumber}
            onChange={handleInputChange}
            placeholder="Booking confirmation number"
          />
        </div>
        <div className="form-group">
          <label htmlFor="phoneNumber">Phone Number</label>
          <input
            type="tel"
            id="phoneNumber"
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handleInputChange}
            placeholder="Contact phone number"
          />
        </div>
        <div className="form-group">
          <label htmlFor="price">Price</label>
          <input
            type="number"
            id="price"
            name="price"
            value={formData.price}
            onChange={handleInputChange}
            placeholder="0.00"
            step="0.01"
            min="0"
          />
        </div>
      </div>

      <div className="form-section">
        <h4>Deposit Information</h4>
        
        <div className="form-row">
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.depositRequired}
                onChange={(e) => setFormData({...formData, depositRequired: e.target.checked})}
              />
              Deposit Required
            </label>
          </div>
        </div>

        {formData.depositRequired && (
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="depositAmount">Deposit Amount*</label>
              <input
                type="number"
                id="depositAmount"
                name="depositAmount"
                value={formData.depositAmount}
                onChange={handleInputChange}
                placeholder="0.00"
                min="0"
                step="0.01"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="depositDueDate">Deposit Due Date</label>
              <input
                type="date"
                id="depositDueDate"
                name="depositDueDate"
                value={formData.depositDueDate}
                onChange={handleInputChange}
              />
            </div>
          </div>
        )}
      </div>

      <div className="form-section">
        <h4>Client Options</h4>
        
        <div className="form-row">
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.isOption}
                onChange={(e) => setFormData({...formData, isOption: e.target.checked})}
              />
              Mark as Optional for Client
            </label>
            <small className="form-help">
              When checked, clients can choose whether to include this component in their booking
            </small>
          </div>
        </div>

        {formData.isOption && (
          <>
            <div className="form-group">
              <label>Option Description for Client</label>
              <textarea
                value={formData.optionDescription}
                onChange={(e) => setFormData({...formData, optionDescription: e.target.value})}
                placeholder="e.g., Optional car rental upgrade or alternative"
                rows="2"
              />
              <small className="form-help">
                This description helps clients understand what this option includes
              </small>
            </div>

            <div className="form-group">
              <label>Payment Timing for This Option</label>
              <select
                name="paymentTiming"
                value={formData.paymentTiming}
                onChange={(e) => setFormData({...formData, paymentTiming: e.target.value})}
                className="form-control"
              >
                <option value="immediate">Pay Immediately When Selected (Full Price)</option>
                <option value="with_deposit">Pay Deposit When Selected</option>
                <option value="with_final">Pay With Final Payment</option>
                <option value="bundled">Bundled (No Additional Payment)</option>
              </select>
              <small className="form-help">
                Controls when payment is due if client selects this option
              </small>
            </div>

            {formData.paymentTiming === 'with_deposit' && (
              <div className="form-group">
                <label>Deposit Amount for This Option ($)</label>
                <input
                  type="number"
                  name="optionalDepositAmount"
                  value={formData.optionalDepositAmount}
                  onChange={(e) => setFormData({...formData, optionalDepositAmount: e.target.value})}
                  step="0.01"
                  min="0"
                  className="form-control"
                  placeholder="0.00"
                />
                <small className="form-help">
                  Amount due immediately when client selects this option
                </small>
              </div>
            )}
          </>
        )}
      </div>

      <AttachmentUpload
        photos={formData.photos}
        documents={formData.documents}
        onPhotosChange={(photos) => setFormData({...formData, photos})}
        onDocumentsChange={(documents) => setFormData({...formData, documents})}
        maxPhotos={5}
        maxDocuments={3}
        showPhotos={true}
        showDocuments={true}
      />

      <div className="form-actions">
        <button type="button" onClick={onCancel} className="btn btn-secondary">
          Cancel
        </button>
        <button type="submit" className="btn btn-primary">
          Save Car Rental
        </button>
      </div>
    </form>
  );
};

export default CarRentalForm;
